import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle, ChevronRight, Clock, DollarSign, Target, TrendingUp, Zap } from "lucide-react";
import { PublicFinalCTA, PublicHeroSection, PublicImpactBar, PublicPageTemplate } from "@/components/public";
import { getCanonicalUrl } from "@/config/site";
import { createBreadcrumbSchema, createFaqPageSchema, createOrganizationSchema, createServiceSchema } from "@/config/seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { getBusinessScenarioBySlug } from "@/data/business/registry";
import { getPlaybookById } from "@/data/playbooks/registry";
import { UserService } from "@/services/users/UserService";

const PublicBusinessPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [isCreating, setIsCreating] = useState(false);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Get business scenario data
  const scenario = getBusinessScenarioBySlug(slug || "");
  const recommendedPlaybooks = scenario ? scenario.recommendedPlaybooks.map((id) => getPlaybookById(id)).filter(Boolean) : [];

  if (!scenario) {
    return (
      <div className="min-h-screen bg-[#F9FAFC] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-2">Business solution not found</h2>
          <Button onClick={() => navigate("/business")}>Back to Business Solutions</Button>
        </div>
      </div>
    );
  }

  const handleGetStarted = async () => {
    const {
      data: { user },
    } = await UserService.getUser();
    if (!user) {
      navigate("/signin");
      return;
    }
    navigate("/user-hub/genie-setup");
  };

  const seoConfig = {
    title: `${scenario.meta.name} - Voice Solutions | Help Genie`,
    description: scenario.meta.description,
    ogTitle: `${scenario.meta.name} - Voice Solutions`,
    ogDescription: scenario.meta.description,
    canonicalUrl: getCanonicalUrl(`/business/${scenario.meta.slug}`),
    structuredData: [
      createBreadcrumbSchema([
        { name: "Home", url: getCanonicalUrl("/") },
        { name: "Business Solutions", url: getCanonicalUrl("/business") },
        { name: scenario.meta.name, url: getCanonicalUrl(`/business/${scenario.meta.slug}`) },
      ]),
      createServiceSchema(scenario.meta.name, scenario.meta.description, getCanonicalUrl(`/business/${scenario.meta.slug}`)),
      ...(scenario.faqs?.length ? [createFaqPageSchema(scenario.faqs)] : []),
      createOrganizationSchema(),
    ],
  };

  return (
    <PublicPageTemplate seo={seoConfig}>
      <PublicHeroSection
        backgroundImage="/analytics-dashboard.webp"
        gradientOverlay="bg-gradient-to-br from-blue-900/80 via-purple-900/70 to-blue-900/80"
        badge={
          <Badge className="bg-white/20 text-white border-white/30">
            {scenario.meta.employeeCount} employees • {scenario.meta.annualRevenue}
          </Badge>
        }
        title={`${scenario.meta.name} Solution`}
        tagline={scenario.meta.tagline}
        description={scenario.meta.description}
        primaryButton={{
          text: isCreating ? "Getting Started..." : "Start Free Trial",
          onClick: handleGetStarted,
          disabled: isCreating,
        }}
        secondaryButton={{
          text: "Try Voice Demo",
          onClick: () => navigate("/live-agent/business-onboarding"),
        }}
        quickStats={
          scenario.metrics ? (
            <div className="flex flex-wrap gap-6 justify-center lg:justify-start">
              <div className="flex items-center gap-2 text-gray-200">
                <TrendingUp className="h-4 w-4" />
                <span className="text-sm font-semibold">{scenario.metrics.averageROI} ROI</span>
              </div>
              <div className="flex items-center gap-2 text-gray-200">
                <Clock className="h-4 w-4" />
                <span className="text-sm font-semibold">{scenario.metrics.implementationTime} setup</span>
              </div>
              <div className="flex items-center gap-2 text-gray-200">
                <DollarSign className="h-4 w-4" />
                <span className="text-sm font-semibold">{scenario.metrics.costSavings} savings</span>
              </div>
            </div>
          ) : undefined
        }
        demo={{
          genieId: scenario.meta.id,
          firstMessage: scenario.demoConfig?.firstMessage || "Welcome! How can I help your business grow today?",
          onDemoClick: () => navigate("/live-agent/business-onboarding"),
          demoButtonText: "Try Live Voice Demo",
          topBadge: `${scenario.meta.size} Business`,
          bottomBadge: "Voice Ready",
          gradientColors: { from: "blue-600", to: "purple-600" },
        }}
      />

      {scenario.metrics && (
        <PublicImpactBar
          gradient="bg-gradient-to-r from-blue-600 to-purple-600"
          stats={[
            { value: scenario.metrics.averageROI, label: "Average ROI" },
            { value: scenario.metrics.supportTicketReduction, label: "Support Reduction" },
            { value: scenario.metrics.implementationTime, label: "Setup Time" },
            { value: scenario.metrics.customerSatisfaction, label: "Satisfaction Score" },
          ]}
        />
      )}

      {/* Challenges & Solutions Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left: Challenges */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Common Challenges You Face</h2>
              <div className="space-y-4">
                {scenario.meta.challenges.map((challenge, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-red-50 rounded-lg">
                    <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                      <span className="text-2xl">⚠️</span>
                    </div>
                    <p className="text-gray-700">{challenge}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Goals */}
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8">Your Business Goals</h2>
              <div className="space-y-4">
                {scenario.meta.goals.map((goal, idx) => (
                  <div key={idx} className="flex items-start gap-4 p-4 bg-green-50 rounded-lg">
                    <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                    </div>
                    <p className="text-gray-700">{goal}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Use Cases */}
      <section className="py-24 md:py-32 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Use Cases for {scenario.meta.name}</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Proven applications that drive real business results</p>
          </div>

          {/* Primary Use Cases */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {scenario.useCases.primary.map((useCase) => {
              const Icon = useCase.icon || Zap;
              return (
                <Card key={useCase.id} className="p-8 hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                  <p className="text-gray-600 mb-4">{useCase.description}</p>
                  {useCase.expectedOutcome && <p className="text-sm font-semibold text-blue-600">{useCase.expectedOutcome}</p>}
                </Card>
              );
            })}
          </div>

          {/* Secondary Use Cases */}
          {scenario.useCases.secondary && scenario.useCases.secondary.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {scenario.useCases.secondary.map((useCase) => {
                const Icon = useCase.icon || Target;
                return (
                  <Card key={useCase.id} className="p-6 bg-white/50">
                    <Icon className="h-8 w-8 text-purple-600 mb-3" />
                    <h4 className="font-semibold mb-2">{useCase.title}</h4>
                    <p className="text-sm text-gray-600">{useCase.description}</p>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Why {scenario.meta.name} Choose Help Genie</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">Tailored solutions that address your specific business needs</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {scenario.benefits.map((benefit, idx) => {
              const Icon = benefit.icon || CheckCircle;
              return (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full mx-auto mb-4 flex items-center justify-center">
                    <Icon className="h-8 w-8 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm mb-2">{benefit.description}</p>
                  {benefit.metric && <Badge className="bg-blue-100 text-blue-700">{benefit.metric}</Badge>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Recommended Playbooks */}
      {recommendedPlaybooks.length > 0 && (
        <section className="py-24 md:py-32 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Recommended Playbooks for {scenario.meta.name}</h2>
              <p className="text-xl text-gray-600 mb-8">Pre-built strategies designed for your business size</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {recommendedPlaybooks.slice(0, 6).map((playbook) => (
                <div
                  key={playbook.meta.id}
                  className="bg-white rounded-xl border-2 border-gray-200 hover:border-blue-400 overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  onClick={() => navigate(`/playbooks/${playbook.meta.categoryId}/${playbook.meta.slug}`)}
                >
                  <div className="p-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">Voice Playbook</span>
                      <div className="flex gap-1">
                        {[...Array(3)].map((_, i) => (
                          <div
                            key={i}
                            className="w-1 h-3 bg-blue-400 rounded-full animate-pulse"
                            style={{ animationDelay: `${i * 0.2}s` }}
                          />
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                        {playbook.meta.name}
                      </h4>
                      <p className="text-sm font-medium text-blue-600 mb-3">{playbook.meta.tagline}</p>
                    </div>

                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3">{playbook.meta.strategy}</p>

                    <div className="pt-4 flex items-center justify-between">
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        {playbook.meta.estimatedSetupTime && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {playbook.meta.estimatedSetupTime}
                          </span>
                        )}
                      </div>
                      <ChevronRight className="h-5 w-5 text-blue-600 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Success Story */}
      {scenario.successStories && scenario.successStories.length > 0 && (
        <section className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Success Story</h2>
              <p className="text-xl text-gray-600">See how businesses like yours are winning with Help Genie</p>
            </div>

            {scenario.successStories.slice(0, 1).map((story, idx) => (
              <Card key={idx} className="p-10 max-w-4xl mx-auto border-2 hover:border-blue-400 transition-all hover:shadow-xl">
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">{story.companyName}</h3>
                      <p className="text-gray-600">
                        {story.companySize} • {story.industry}
                      </p>
                    </div>
                    <GenieAvatar size={80} />
                  </div>

                  <div className="grid lg:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Challenge</h4>
                      <p className="text-gray-600">{story.challenge}</p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Solution</h4>
                      <p className="text-gray-600">{story.solution}</p>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="font-semibold text-gray-900 mb-4">Results</h4>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {story.results.map((result, ridx) => (
                        <div key={ridx} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{result}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {story.quote && (
                    <blockquote className="border-l-4 border-blue-600 pl-6 py-2">
                      <p className="text-lg italic text-gray-700 mb-2">"{story.quote.text}"</p>
                      <footer className="text-sm text-gray-600">
                        — {story.quote.author}, {story.quote.title}
                      </footer>
                    </blockquote>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </section>
      )}

      {/* FAQs */}
      {scenario.faqs && scenario.faqs.length > 0 && (
        <section className="py-24 md:py-32 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            </div>

            <div className="space-y-6">
              {scenario.faqs.map((faq, idx) => (
                <Card key={idx} className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      <PublicFinalCTA
        backgroundImage="/hero-background.webp"
        gradientOverlay="bg-gradient-to-br from-blue-600 to-purple-600"
        title={`Ready to Transform Your ${scenario.meta.name}?`}
        description={`Join thousands of ${scenario.meta.size} businesses already using Help Genie to scale smarter`}
        primaryButton={{
          text: isCreating ? "Getting Started..." : "Start Free Trial",
          onClick: handleGetStarted,
          disabled: isCreating,
        }}
        secondaryButton={{
          text: "Schedule Demo",
          onClick: () => navigate("/contact"),
        }}
        disclaimer={`No credit card required • ${scenario.metrics?.implementationTime || "2-3 days"} setup • Cancel anytime`}
      />
    </PublicPageTemplate>
  );
};

export default PublicBusinessPage;
